import { defineComponent, ref, computed, onMounted } from '@vue/composition-api';
import { respondToBelow, ScreenWidth } from '@superbet-group/web.lib.vue-utilities';
import __vue_component__ from '../SdsIcon/SdsIcon.vue.js';
import __vue_component__$1 from '../SdsBrandImage/SdsBrandImage.vue.js';
import __vue_component__$2 from '../SdsButtonIcon/SdsButtonIcon.vue.js';
import __vue_component__$3 from '../SdsIllustration/SdsIllustration.vue.js';
import { useSwipe } from '../utils/useSwipe.js';
import { useScrollingTracker } from '../utils/useScrollingTracker.js';

var script = defineComponent({
    name: 'SdsModal',
    props: {
        title: {
            type: String,
            required: false,
        },
        subtitle: {
            type: String,
            required: false,
        },
        size: {
            type: String,
            required: false,
            default: 'md',
        },
        styling: {
            type: String,
            required: false,
            default: '',
        },
        neutralIconName: {
            type: String,
            required: false,
            default: '',
        },
        illustrationUrl: {
            type: String,
            required: false,
            default: undefined,
        },
        sdsIllustrationName: {
            type: String,
            required: false,
            default: undefined,
        },
        isDismissable: {
            type: Boolean,
            required: false,
            default: true,
        },
        areActionsStacked: {
            type: Boolean,
            required: false,
            default: false,
        },
        hasMobileTransition: {
            type: Boolean,
            required: false,
            default: true,
        },
    },
    emits: [
        'click:primary',
        'click:secondary',
        'close',
    ],
    setup: function (props, _a) {
        var emit = _a.emit;
        var elementRef = ref();
        var contentElementRef = ref();
        var isFullyShown = ref(false);
        // We're not telling it to use desktop mouse motions, because this should only work on phones & tablets
        var onUpSwipe = useSwipe(elementRef).onUpSwipe;
        var isContextualBrandIllustration = computed(function () { return props.styling != null && props.styling !== 'neutral'; });
        var contextualIcon = computed(function () {
            switch (props.styling) {
                case 'warning':
                    return 'statusWarning';
                case 'danger':
                    return 'statusDanger';
                case 'success':
                    return 'statusSuccess';
                case 'info':
                    return 'statusInfo';
                case 'neutral':
                    return props.neutralIconName;
            }
        });
        var _b = useScrollingTracker(contentElementRef), isScrollable = _b.isScrollable, isScrolled = _b.isScrolled;
        var isMobile = computed(function () { return respondToBelow(ScreenWidth.tLg); });
        var onClose = function () {
            var _a;
            if (!isMobile.value) {
                emit('close');
                return;
            }
            (_a = elementRef.value) === null || _a === void 0 ? void 0 : _a.addEventListener('transitionend', function () {
                emit('close');
            }, { once: true });
            isFullyShown.value = false;
        };
        // These needs to be declared statically, otherwise they get recomputed all the time
        var primaryActionAttrs = {
            size: 'lg',
            variant: 'primary',
            'data-action': 'primary',
        };
        var primaryActionHandlers = {
            click: function () { return emit('click:primary'); },
        };
        var secondaryActionAttrs = {
            size: 'lg',
            variant: 'secondary',
            'data-action': 'secondary',
        };
        var secondaryActionHandlers = {
            click: function () { return emit('click:secondary'); },
        };
        var dismissActionAttrs = {
            size: 'lg',
            variant: 'tertiary',
            'data-action': 'dismiss',
        };
        var dismissActionHandlers = {
            click: onClose,
        };
        onUpSwipe(function () {
            if (props.isDismissable && !isScrollable.value) {
                onClose();
            }
        });
        onMounted(function () {
            if (!props.hasMobileTransition) {
                isFullyShown.value = true;
                return;
            }
            setTimeout(function () {
                isFullyShown.value = true;
            });
        });
        return {
            elementRef: elementRef,
            contentElementRef: contentElementRef,
            isFullyShown: isFullyShown,
            isMobile: isMobile,
            isScrolled: isScrolled,
            isScrollable: isScrollable,
            primaryActionAttrs: primaryActionAttrs,
            primaryActionHandlers: primaryActionHandlers,
            secondaryActionAttrs: secondaryActionAttrs,
            secondaryActionHandlers: secondaryActionHandlers,
            dismissActionAttrs: dismissActionAttrs,
            dismissActionHandlers: dismissActionHandlers,
            isContextualBrandIllustration: isContextualBrandIllustration,
            contextualIcon: contextualIcon,
            onClose: onClose,
        };
    },
    components: {
        SdsIcon: __vue_component__,
        SdsBrandImage: __vue_component__$1,
        SdsButtonIcon: __vue_component__$2,
        SdsIllustration: __vue_component__$3,
    },
});

export default script;
